<script setup lang="ts">
import { storeToRefs } from 'pinia';
import type { MenuItem } from 'primevue/menuitem';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { useProductGroupStore } from 'shared/products/groups/stores/productGroupStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useProductGroupAuditApi } from './api/productGroupAuditApi';
import type { DataTableRowClickEvent, DataTableRowDoubleClickEvent } from 'primevue/datatable';
import { Button, Column, DataTable, Card, InputText, Menu } from 'primevue';
import MainLayout from '@/common/components/MainLayout.vue';
import EditProductGroupDialog from './components/EditProductGroupDialog.vue';
import DeleteProductGroupDialog from './components/DeleteProductGroupDialog.vue';
import AuditDialog from 'shared/audit/components/AuditDialog.vue';
import { useRefereshProductGroupStore } from './commands/refreshProductGroupStore';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const store = useProductGroupStore();
const refereshProductGroupStore = useRefereshProductGroupStore();

const productGroupAuditApi = useProductGroupAuditApi();

const filters = ref({
  global: {
    value: null,
    matchMode: "contains"
  }
});

const groups = computed<ProductGroupItem[]>(() => {
  const result = store.groups.map(g => {
    return {
      id: g.id,
      name: g.name,
      type: translations.value.productType(g.productType),
      color: g.color,
      nameSortOrder: 0
    }
  });

  result.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  for (let i = 0; i < result.length; i++) {
    const item = result[i];
    if (item) {
      item.nameSortOrder = i;
    }
  }

  return result;
});

const selectedGroups = ref<ProductGroupItem[]>([]);
const loading = ref(false);
const menuGroupId = ref<string>();

const groupMenu = useTemplateRef('group-menu');
const editDialog = useTemplateRef('edit-dialog');
const deleteDialog = useTemplateRef('delete-dialog');
const auditDialog = useTemplateRef('audit-dialog');
const actionFailedAlert = useActionFailedAlert();

onMounted(async () => {
  refereshProductGroupStore.initialize();
});

const menuItems = computed<MenuItem[]>(() => [
  {
    label: translations.value.generic.edit,
    icon: 'pi pi-pencil',
    command() {
      const id = menuGroupId.value;
      if (!id) return;
      show(id);
    },
  },
  {
    label: translations.value.generic.delete,
    icon: 'pi pi-trash',
    command() {
      const id = menuGroupId.value;
      if (!id) return;
      deleteDialog.value?.show(id);
    }
  },
  {
    label: translations.value.generic.audit,
    icon: 'pi pi-search',
    async command() {
      const id = menuGroupId.value;
      if (!id) return;

      const response = await productGroupAuditApi.execute({
        id: id
      });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      auditDialog.value?.show({
        audit: response.audit
      });
    }
  }
]);

function onAddNewClick() {
  editDialog.value?.show({});
}

function onDoubleClick(ev: DataTableRowDoubleClickEvent) {
  const group = ev.data as ProductGroupItem;
  show(group.id);
}

function show(id: string) {
  editDialog.value?.show({ groupId: id });
}

function onClick(ev: DataTableRowClickEvent) {
  const target = ev.originalEvent.target as HTMLElement | null;
  if (!target) return;
  const name = target.closest('.product-group-name');
  if (!name) return;
  show(ev.data.id);
}

function toggleMenu(ev: Event, groupId: string) {
  menuGroupId.value = groupId;
  groupMenu.value?.toggle(ev);
}

interface ProductGroupItem {
  id: string;
  name: string;
  type: string;
  color: string | null;
  nameSortOrder: number;
}

</script>

<template>
  <MainLayout>
    <Card class="product-group-card">
      <template #title>
        <span>{{ translations.productGroups.listPage.title }}</span>
        <Button class="add-button" :label="translations.generic.add" @click="onAddNewClick">
        </Button>
        <Button class="products-button">
          <RouterLink to="/products">{{ translations.generic.products }}</RouterLink>
        </Button>
        <InputText class="search-field" autofocus v-model="filters.global.value"
          :placeholder="translations.generic.search" />
      </template>
      <template #content>
        <DataTable class="product-group-table" sort-field="name" v-model:selection="selectedGroups"
          v-model:filters="filters" :value="groups" data-key="id" scrollable scrollHeight="flex" meta-key-selection
          @row-dblclick="onDoubleClick" :loading="loading" @row-click="onClick">
          <template #empty>{{ translations.productGroups.listPage.empty }}</template>
          <template #loading>{{ translations.productGroups.listPage.loading }}</template>
          <Column field="name" class="product-group-name" :header="translations.generic.name" sortable
            sort-field="nameSortOrder">
          </Column>
          <Column field="type" :header="translations.generic.type" sortable></Column>
          <Column field="code" :header="translations.generic.code" sortable></Column>
          <Column :header="translations.generic.color">
            <template #body="group">
              <div v-if="group.data.color" class="flex gap-2">
                <div class="w-6 h-6 rounded" :style="{ backgroundColor: `${group.data.color}` }">
                </div>
              </div>
            </template>
          </Column>
          <Column class="w-24 !text-end">
            <template #body="group">
              <Button icon="pi pi-ellipsis-v" variant="text" severity="secondary" rounded
                @click="(ev) => toggleMenu(ev, group.data.id)"></Button>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>
  </MainLayout>

  <EditProductGroupDialog ref="edit-dialog"></EditProductGroupDialog>
  <DeleteProductGroupDialog ref="delete-dialog"></DeleteProductGroupDialog>
  <AuditDialog ref="audit-dialog"></AuditDialog>
  <Menu ref="group-menu" :model="menuItems" :popup="true"></Menu>
</template>

<style scoped>
:deep(.p-datatable-hoverable .p-datatable-selectable-row) {
  cursor: unset;
}

:deep(.p-card-title) {
  display: grid;
  grid-template-columns: auto 1fr auto auto 1rem auto;
  column-gap: 0.5rem;
  align-items: center;
}

.add-button {
  grid-column: 3;
}

.products-button {
  grid-column: 4;
}

.search-field {
  grid-column: 6;
}

:deep(.product-group-name) {
  cursor: pointer;
}

:deep(.product-group-name:hover) {
  color: var(--p-primary-900);
  background-color: var;
  text-decoration: underline;
}

.product-group-card {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-body) {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-content) {
  overflow-y: hidden;
  height: 100%;
}
</style>
